import { call, put, select } from 'redux-saga/effects'
import encodeBazaarVoiceContent from '../../helpers/encodeBazaarVoiceContent'
import formatTimestampToDateHelper from '../../helpers/formatTimestampToDate'
import Logger from '../../helpers/Logger'

import { fetchApi } from '../helpers/fetchApi'

import {
  fetchBVQuestionAnswerFailure,
  fetchBVQuestionAnswerSuccess
} from '../../actionCreators/bazaarVoiceQuestionAnswer/bazaarVoiceQuestionAnswerActionCreators'
import { isValidSku } from '../../helpers/skuValidator'

const logger = Logger.getInstance()
const endpoint = '/product-api/bazaar-voice-question-answer/partNumber'

const formatTimestampToDate = (...args) => {
  const [timestamp] = args
  if (!timestamp) {
    return null
  }
  logger.debug('functionLogger', { arguments: args }, 'BazaarVoiceReviewStore formatTimestampToDate')
  return formatTimestampToDateHelper(timestamp)
}

const generateStructuredStats = (partNumber, data) => {
  const { Includes, TotalResults, Offset } = data
  logger.debug('functionLogger', { arguments: data }, 'BazaarVoiceQuestionAnswerStore generateStructuredStats')
  let answerCount = 0

  if (Includes.Products) {
    const SKUS = Object.keys(Includes.Products)
    const currentSkuStats = Includes.Products[partNumber]
    const alternativeSkuStats = Includes.Products[SKUS[0]]
    answerCount = currentSkuStats
      ? currentSkuStats.QAStatistics.TotalAnswerCount
      : alternativeSkuStats.QAStatistics.TotalAnswerCount
  }

  return {
    attributes: {
      Offset,
      questionCount: TotalResults || 0,
      answerCount
    }
  }
}

const generateStructuredQuestion = (...args) => {
  const [question, fullResponse] = args

  const contextDataValues = question.ContextDataValues

  return {
    id: question.Id,
    type: 'question',
    attributes: {
      title: question.QuestionSummary,
      content: encodeBazaarVoiceContent(question.QuestionDetails),
      submitted: formatTimestampToDate(question.SubmissionTime),
      profile: {
        name: question.UserNickname,
        location: question.UserLocation,
        contextAttributes: Object.keys(question.ContextDataValues).reduce((obj, contextKey) => {
          obj[contextDataValues[contextKey].DimensionLabel] = contextDataValues[contextKey].ValueLabel // eslint-disable-line
          return obj
        }, {}),
        badges: []
      },
      answers: question.AnswerIds.map(answerId => {
        const answer = fullResponse.Includes.Answers[answerId]
        return {
          content: answer.AnswerText,
          id: parseInt(answer.Id, 10),
          helpfulness: {
            helpful: answer.TotalPositiveFeedbackCount,
            unhelpful: answer.TotalNegativeFeedbackCount
          },
          profile: {
            name: answer.UserNickname,
            submitted: formatTimestampToDate(answer.SubmissionTime),
            location: answer.UserLocation,
            isStaff: !!answer.Badges.Staff,
            contextAttributes: Object.keys(answer.ContextDataValues).reduce((obj, contextKey) => {
              obj[answer.ContextDataValues[contextKey].DimensionLabel] = answer.ContextDataValues[contextKey].ValueLabel // eslint-disable-line
              return obj
            }, {}),
            badges: Object.keys(answer.Badges)
              .map(badge => answer.Badges[badge].Id)
              .concat([answer.BrandImageLogoURL])
              .filter(badge => badge)
          }
        }
      })
    }
  }
}

const generateSuccessAction = (partNumber, oldData, stats, paginatedQuestions, searchTerm = null, cache) => {
  let questions = oldData.productQuestions ? oldData.productQuestions[partNumber] : undefined
  if (!questions) {
    questions = paginatedQuestions
  }
  const newData = {
    questions,
    stats,
    paginatedQuestions,
    cache,
    searchTerm
  }
  return fetchBVQuestionAnswerSuccess({ partNumber, newData })
}

const bazaarVoiceQuestionAnswer = state => state.bazaarVoiceQuestionAnswer

function* bazaarVoiceQuestionAnswerSaga(action, checkCache = true, baseUrl, cookies) {
  const { payload } = action
  const { partNumber, query } = payload
  const url = `${baseUrl || ''}${endpoint}/${partNumber}`

  try {
    if (!isValidSku(partNumber)) {
      const error = new Error('Invalid part number')
      error.status = 400
      throw error
    }
    const { data, meta } = yield select(bazaarVoiceQuestionAnswer)
    const cacheId = `${payload.partNumber}_${query.Limit}_${query.Offset}_${query.Sort}_${query.Search}`
    // Case 1: Check cache to see if you already have data for part number
    if (checkCache) {
      const { cache } = data
      const isInCache = cache[cacheId] && !meta[partNumber].errorDetail
      if (isInCache) {
        const stats = generateStructuredStats(partNumber, cache[cacheId].data)
        const paginatedQuestions = cache[cacheId].data.Results.map(question =>
          generateStructuredQuestion(question, cache[cacheId].data)
        )
        const successAction = generateSuccessAction(partNumber, data, stats, paginatedQuestions, query.Search)
        return yield put(successAction)
      }
    }

    // Case 2: Make Ajax call to get data for part number
    let headers = {}
    if (cookies) {
      headers = {
        Cookie: cookies,
        ...headers
      }
    }
    const jsonData = yield call(fetchApi, url, { ...query, returnMeta: true }, headers)
    const stats = generateStructuredStats(partNumber, jsonData.data)
    const paginatedQuestions = jsonData.data.Results.map(question =>
      generateStructuredQuestion(question, jsonData.data)
    )
    const cache = {
      [cacheId]: jsonData
    }
    const successAction = generateSuccessAction(partNumber, data, stats, paginatedQuestions, query.Search, cache)
    return yield put(successAction)
  } catch (error) {
    const data = {
      partNumber,
      errorDetail: error.toString()
    }
    if (error.response && error.response.body && error.response.body.errors) {
      data.errorDetail = error.response.body.errors
    }
    const failureAction = fetchBVQuestionAnswerFailure(data)
    return yield put(failureAction)
  }
}

export { endpoint, bazaarVoiceQuestionAnswer, bazaarVoiceQuestionAnswerSaga }
